import { useMutation, useQuery } from "@tanstack/react-query";
import axios, { AxiosResponse } from "axios";
import { httpClient, ROOT_URL } from "../App";
import { TOKEN_KEY } from "../components/providers/authProvider";

export type IBodyProfile = {
  email: string;
  fullName: string;
  regNo: string;
  country: string;
  // userId: string;
  address: string;
  website: string;
  avatar: string;
  smartUID?: string;
};

export const useGetProfile = () => {
  return useQuery([`/api/v1/auth/profile`], async () => getProfile());
};

export function getProfile() {
  return httpClient.get(`${ROOT_URL}/api/v1/auth/profile`);
}

export const useUpdateProfile = (config = {}) => {
  return useMutation({
    ...config,
    mutationFn: updateProfile,
  });
};

export function updateProfile(
  body: IBodyProfile & {
    avatar_uuid?: string;
    onboardingFormUuid?: string;
    onboardingFileName?: string;
  }
) {
  return httpClient.patch(`${ROOT_URL}/api/v1/auth/update-profile`, body);
}

export const useUploadAvatar = (config = {}) => {
  return useMutation({
    ...config,
    mutationFn: uploadAvatar,
  });
};

export function uploadAvatar(body: any) {
  body.append("type", "avatar");
  return axios.post(`${ROOT_URL}/api/v1/storage/upload-file`, body, {
    headers: {
      Authorization: `Bearer ${localStorage.getItem(TOKEN_KEY)}`,
    },
  });
}

export const useGetAvatar = (avatar_uuid: string) => {
  return useQuery(
    [`/api/v1/storage/get-url`, avatar_uuid],
    async () => getAvatar(avatar_uuid),
    { enabled: !!avatar_uuid }
  );
};

export function getAvatar(avatar_uuid: string) {
  return httpClient.get(`${ROOT_URL}/api/v1/storage/get-url/${avatar_uuid}`);
}

/** Upload onboarding form file to UserStorageFolder.OnboardingSMARTForm */
export const useUploadOnboardingForm = (config = {}) => {
  return useMutation({
    ...config,
    mutationFn: uploadOnboardingForm,
  });
};

export function uploadOnboardingForm(body: FormData) {
  body.append("type", "onboarding_form");
  return axios.post(`${ROOT_URL}/api/v1/storage/upload-file`, body, {
    headers: {
      Authorization: `Bearer ${localStorage.getItem(TOKEN_KEY)}`,
    },
  });
}

/** Get file URL by uuid (used for onboarding form document) */
export const useGetStorageFileUrl = (uuid?: string | null | undefined) => {
  return useQuery(
    [`/api/v1/storage/get-url`, uuid],
    async () => getStorageFileUrl(uuid),
    { enabled: !!uuid }
  );
};

export function getStorageFileUrl(uuid?: string | null | undefined) {
  return httpClient.get(`${ROOT_URL}/api/v1/storage/get-url/${uuid}`);
}

export const useGetInfoByUserCode = (config = {}) => {
  return useMutation({
    ...config,
    mutationFn: getInfoByUserCode,
  });
};

export function getInfoByUserCode(userCode: string) {
  return httpClient.get(`${ROOT_URL}/api/v1/users/code/${userCode}`);
}

/** Row from `GET /users/smart-uid/:query` (search). Value used on select is always `smartUID`. */
export type SmartUidSuggestionItem = {
  smartUID: string;
  fullName?: string;
  email?: string;
};

/**
 * Dropdown label: UID → fullName → email (for display only).
 */
export function formatSmartUidSuggestionLabel(item: SmartUidSuggestionItem): string {
  const uid = item.smartUID?.trim();
  if (uid) return uid;
  const name = item.fullName?.trim();
  if (name) return name;
  const em = item.email?.trim();
  if (em) return em;
  return "";
}

function userRecordToSearchRow(
  o: Record<string, unknown>
): SmartUidSuggestionItem | null {
  const smartUID =
    (typeof o.smartUID === "string" && o.smartUID) ||
    (typeof o.smartUid === "string" && o.smartUid) ||
    (typeof o.userId === "string" && o.userId) ||
    (typeof o.user_id === "string" && o.user_id) ||
    (typeof o.userCode === "string" && o.userCode) ||
    (typeof o.code === "string" && o.code) ||
    "";
  if (!smartUID.trim()) return null;
  return {
    smartUID: smartUID.trim(),
    fullName:
      typeof o.fullName === "string"
        ? o.fullName
        : typeof o.name === "string"
          ? o.name
          : undefined,
    email: typeof o.email === "string" ? o.email : undefined,
  };
}

function mapSearchList(raw: unknown[]): SmartUidSuggestionItem[] {
  const out: SmartUidSuggestionItem[] = [];
  for (const item of raw) {
    if (typeof item === "string" && item.trim()) {
      out.push({ smartUID: item.trim() });
      continue;
    }
    if (!item || typeof item !== "object") continue;
    const row = userRecordToSearchRow(item as Record<string, unknown>);
    if (row) out.push(row);
  }
  return out;
}

/** Search / typeahead only — `GET /api/v1/users/smart-uid/:query`. Does not validate code. */
export function searchUsersBySmartUidPrefix(query: string) {
  const q = query.trim();
  const encoded = encodeURIComponent(q);
  return httpClient.get(`${ROOT_URL}/api/v1/users/smart-uid/${encoded}`);
}

/**
 * Parse response from `searchUsersBySmartUidPrefix` (array or nested list in `data`).
 */
export function parseSmartUidSearchResponse(
  response: AxiosResponse<unknown>
): SmartUidSuggestionItem[] {
  const body = response?.data as Record<string, unknown> | unknown[] | undefined;

  if (Array.isArray(body)) {
    return mapSearchList(body);
  }

  if (body && typeof body === "object") {
    const d = body as Record<string, unknown>;
    const nested = d.data ?? d.items ?? d.suggestions ?? d.results;

    if (Array.isArray(nested)) {
      return mapSearchList(nested);
    }

    if (nested && typeof nested === "object" && !Array.isArray(nested)) {
      const inner = nested as Record<string, unknown>;
      if (Array.isArray(inner.data)) {
        return mapSearchList(inner.data as unknown[]);
      }
      if (
        inner.data &&
        typeof inner.data === "object" &&
        !Array.isArray(inner.data)
      ) {
        const single = userRecordToSearchRow(
          inner.data as Record<string, unknown>
        );
        if (single) return [single];
      }
      const single = userRecordToSearchRow(inner);
      if (single) return [single];
    }
  }

  return [];
}

export const usePrintStatementReport = (config = {}) => {
  return useMutation({
    ...config,
    mutationFn: printStatementReport,
  });
};

export function printStatementReport(body: {
  startDate: string;
  endDate: string;
  showAllBalances?: boolean;
}) {
  return httpClient.post(
    `${ROOT_URL}/api/v1/period-balances/send-report`,
    body
  );
}
